import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyTimesheet, getMyInvoices, downloadMyInvoicePdf } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Sprout, ChevronLeft, ChevronRight, Loader2, FileText, DollarSign,
  Clock, Download, User, LogOut, CheckCircle, XCircle
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(m, delta) {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonth(m) {
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo) - 1]} ${y}`;
}

export default function MyTimesheetPage() {
  const { user, logout } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [tsData, setTsData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('timesheet');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tsRes, invRes] = await Promise.all([getMyTimesheet(month), getMyInvoices()]);
      setTsData(tsRes.data);
      setInvoices(invRes.data.invoices || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDownload = async (id, num) => {
    try {
      await downloadMyInvoicePdf(id, num);
    } catch (err) {
      toast.error('Failed to download');
    }
  };

  const ts = tsData?.timesheet;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center"><Sprout className="w-5 h-5 text-white" /></div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">Timesheet & Pay</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard"><Button variant="outline" size="sm" className="text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Dashboard</Button></Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="text-xs"><User className="w-3 h-3 mr-1" /> {user?.name}</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-center px-4">
            <p className="text-sm font-semibold text-[#111827]" data-testid="month-display">{formatMonth(month)}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setMonth(currentMonth())} className="text-xs text-[#002FA7]">Current</Button>
        </div>

        {/* Tab Toggle */}
        <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-lg w-fit mx-auto" data-testid="my-ts-tabs">
          {[
            { id: 'timesheet', label: 'Timesheet', icon: Clock },
            { id: 'invoices', label: 'My Invoices', icon: FileText },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === tab.id ? 'bg-white text-[#111827] shadow-sm' : 'text-[#4B5563] hover:text-[#111827]'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            {activeView === 'timesheet' && (
              <>
                {!tsData?.has_timesheet ? (
                  <Card className="border border-dashed border-gray-300" data-testid="no-timesheet">
                    <CardContent className="p-10 text-center">
                      <Clock className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
                      <h2 className="text-lg font-bold text-[#111827] mb-2">No Timesheet</h2>
                      <p className="text-sm text-[#4B5563]">No timesheet generated for {formatMonth(month)} yet.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      <Card className="border border-black/10" data-testid="ts-hours"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#111827]">{ts.total_hours}h</p><p className="text-xs text-[#4B5563]">Total Hours</p></CardContent></Card>
                      <Card className="border border-black/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#4B5563]">&pound;{(ts.hourly_rate || 0).toFixed(2)}/hr</p><p className="text-xs text-[#4B5563]">Rate</p></CardContent></Card>
                      <Card className="border border-black/10" data-testid="ts-amount"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#002FA7]">&pound;{(ts.total_amount || 0).toFixed(2)}</p><p className="text-xs text-[#4B5563]">Total Pay</p></CardContent></Card>
                    </div>

                    <Card className="border border-black/10" data-testid="ts-entries">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-base">Sessions — {formatMonth(month)}</CardTitle>
                          <Badge className={ts.status === 'locked' ? 'bg-gray-100 text-gray-700' : 'bg-green-50 text-green-700'}>
                            {ts.status === 'locked' ? 'Locked' : 'Active'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Day</TableHead>
                              <TableHead>Time</TableHead>
                              <TableHead>Subject</TableHead>
                              <TableHead>Student</TableHead>
                              <TableHead>Hours</TableHead>
                              <TableHead>Source</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(ts.entries || []).map((e, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-sm">{e.date}</TableCell>
                                <TableCell className="text-sm font-medium">{DAY_LABELS[e.day] || e.day}</TableCell>
                                <TableCell className="text-sm">{e.start_time} – {e.end_time}</TableCell>
                                <TableCell className="text-sm">{e.subject || '-'}</TableCell>
                                <TableCell className="text-sm text-[#4B5563]">{e.student_name || '-'}</TableCell>
                                <TableCell className="text-sm font-medium">{e.hours}h</TableCell>
                                <TableCell><Badge variant="outline" className="text-xs">{e.source === 'standby_assigned' ? 'Standby' : 'Regular'}</Badge></TableCell>
                              </TableRow>
                            ))}
                            {(ts.entries || []).length === 0 && (
                              <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#9CA3AF] py-6">No entries</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}

            {activeView === 'invoices' && (
              <div className="space-y-3">
                {invoices.length === 0 ? (
                  <Card className="border border-dashed border-gray-300" data-testid="no-invoices">
                    <CardContent className="p-10 text-center">
                      <FileText className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
                      <h2 className="text-lg font-bold text-[#111827] mb-2">No Invoices</h2>
                      <p className="text-sm text-[#4B5563]">No invoices have been generated yet.</p>
                    </CardContent>
                  </Card>
                ) : invoices.map(inv => (
                  <Card key={inv.id} className="border border-black/10" data-testid={`invoice-${inv.id}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#F9FAFB] border border-gray-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#002FA7]" />
                        </div>
                        <div>
                          <p className="font-mono font-medium text-sm">{inv.invoice_number}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#9CA3AF]">{formatMonth(inv.month)}</span>
                            <span className="text-xs text-[#9CA3AF]">{inv.total_hours}h</span>
                            <span className="text-sm font-bold text-[#002FA7]">&pound;{(inv.total_amount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {inv.status === 'paid'
                          ? <Badge className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" /> Paid</Badge>
                          : <Badge className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>
                        }
                        <Button variant="outline" size="sm" onClick={() => handleDownload(inv.id, inv.invoice_number)} data-testid={`download-${inv.id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> PDF
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
